import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import ProjectNav from "../components/ProjectNav";

export default function Reports() {
  const [reports, setReports] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterQuarter, setFilterQuarter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/api/reports?project_id=" + (localStorage.getItem("active_project_id") || "")).then(r => setReports(r.data));
  }, []);

  const quarters = Array.from(new Set(reports.map(r => r.quarter))).sort().reverse();

  const filtered = reports
    .filter(r => {
      const matchSearch = !search || r.version_label.toLowerCase().includes(search.toLowerCase()) || r.quarter.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === "all" || r.status === filterStatus;
      const matchQuarter = filterQuarter === "all" || r.quarter === filterQuarter;
      return matchSearch && matchStatus && matchQuarter;
    })
    .sort((a, b) => {
      if (sortBy === "date_desc") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === "date_asc") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === "cpi_asc") return (a.cpi || 0) - (b.cpi || 0);
      if (sortBy === "cpi_desc") return (b.cpi || 0) - (a.cpi || 0);
      return 0;
    });

  const cpiColor = (cpi: number) => {
    if (!cpi) return "text-gray-400";
    if (cpi < 0.9) return "text-red-500";
    if (cpi < 0.95) return "text-amber-500";
    return "text-green-600";
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const handleDelete = async (id: string) => {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      return;
    }
    try {
      await api.delete(`/api/reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      setDeleteConfirmId(null);
    } catch (e) {
      alert("Failed to delete report. Please try again.");
    }
  };

  const handlePublish = async (id: string) => {
    await api.post(`/api/reports/${id}/publish`);
    setReports(prev => prev.map(r => r.id === id ? { ...r, status: "published" } : r));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={localStorage.getItem("active_project_id") || ""} active="history" />

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Report History</h1>
            <p className="text-xs text-gray-400 mt-0.5">M170 BP884 — All versions</p>
          </div>
          <button onClick={() => navigate("/upload/" + (localStorage.getItem("active_project_id") || "") || "/projects")}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
            + New Report
          </button>
        </div>

        {/* Search and filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by version or quarter..."
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <select value={filterQuarter} onChange={e => setFilterQuarter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="all">All Quarters</option>
              {quarters.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="date_desc">Date (newest)</option>
              <option value="date_asc">Date (oldest)</option>
              <option value="cpi_asc">CPI (lowest)</option>
              <option value="cpi_desc">CPI (highest)</option>
            </select>
            {(search || filterStatus !== "all" || filterQuarter !== "all") && (
              <button onClick={() => { setSearch(""); setFilterStatus("all"); setFilterQuarter("all"); }}
                className="text-xs text-blue-600 hover:underline">Clear filters</button>
            )}
          </div>
        </div>

        {/* Quick insights */}
        {reports.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Quick Insights (Last 4 Quarters)</p>
            <div className="flex items-center gap-6 text-xs">
              <div>
                <span className="text-gray-400">CPI Trend: </span>
                {reports.slice(0, 4).reverse().map((r, i) => (
                  <span key={i} className={cpiColor(r.cpi)}>{r.cpi?.toFixed(3)}{i < 3 ? " → " : ""}</span>
                ))}
                <span className="text-red-500 ml-1">↘</span>
              </div>
              <div>
                <span className="text-gray-400">Total versions: </span>
                <span className="font-medium text-gray-900">{reports.length}</span>
              </div>
              <div>
                <span className="text-gray-400">Published: </span>
                <span className="font-medium text-green-600">{reports.filter(r => r.status === "published").length}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Showing {filtered.length} of {reports.length} reports</p>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Version","Quarter","Created","Status","CPI","VAC","Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r: any) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900">{r.version_label}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.quarter}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium
                      ${r.status === "published" ? "bg-green-50 text-green-600 border border-green-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`}>
                      {r.status === "published" ? "📊 Published" : "📝 Draft"}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm font-bold ${cpiColor(r.cpi)}`}>
                    {r.cpi?.toFixed(3) || "—"}
                    {r.cpi < 0.9 ? " 🔴" : r.cpi < 0.95 ? " 🟡" : r.cpi ? " 🟢" : ""}
                  </td>
                  <td className="px-4 py-3 text-sm text-red-500 font-medium">
                    {r.vac ? `-$${(Math.abs(r.vac)/1000000).toFixed(1)}M` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/reports/${r.id}`)}
                        className="text-xs text-blue-600 hover:underline">View</button>
                      <button onClick={() => {
                        const data = JSON.stringify(r.output, null, 2);
                        const blob = new Blob([data], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${r.version_label}-report.json`;
                        a.click();
                      }} className="text-xs text-green-600 hover:underline">Download</button>
                      <button onClick={() => navigate(`/compare?a=${r.id}`)}
                        className="text-xs text-gray-500 hover:underline">Compare</button>
                      {r.status === "draft" && (
                        <button onClick={() => handlePublish(r.id)}
                          className="text-xs text-green-600 hover:underline">Publish</button>
                      )}
                      <button onClick={() => handleDelete(r.id)}
                        className={`text-xs hover:underline ${deleteConfirmId === r.id ? "text-red-600 font-bold" : "text-red-400"}`}>
                        {deleteConfirmId === r.id ? "Confirm?" : "Delete"}
                      </button>
                      {deleteConfirmId === r.id && (
                        <button onClick={() => setDeleteConfirmId(null)}
                          className="text-xs text-gray-400 hover:underline ml-2">Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                  {reports.length === 0 ? "No reports yet. Generate your first report." : "No reports match your filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
